import type { NoticeDataWidgetLayer } from './types.js';
import type { NoticeWidgetData } from './noticeWidgets.js';
import { WEATHER_ICON_PATHS, WEATHER_ICON_VIEWBOX, type WeatherIconName } from './weatherIcons.js';
import { cssFontFamily } from './timeWidget.js';

export type WeatherObservation = { placeName: string; temperatureC: number; humidityPercent: number; windKmh: number; code: number };
export type WeatherWidgetSize = { width: number; height: number };
/** WMO code → Korean label. KMA (our only source) maps SKY 맑음/구름많음/흐림 to 0/2/3 and PTY
 * 빗방울/소나기/눈날림 to 51/80/77 (see kmaWeather.ts); code 1 never arrives from KMA. */
export function weatherCondition(code: number): string | null {
  return code === 0 ? '맑음' : code === 1 ? '대체로 맑음' : code === 2 ? '구름 많음' : code === 3 ? '흐림' : [45, 48].includes(code) ? '안개'
    : [51, 53, 55].includes(code) ? '빗방울' : [80, 81, 82].includes(code) ? '소나기' : [56, 57, 61, 63, 65, 66, 67].includes(code) ? '비'
      : [68, 69].includes(code) ? '비/눈' : code === 77 ? '눈날림' : [71, 73, 75, 85, 86].includes(code) ? '눈' : [95, 96, 99].includes(code) ? '뇌우' : null;
}
/** Night = 19:00–05:59 local time; clear/partly cloudy skies switch to the moon variants. */
export const isNightHour = (now: Date): boolean => now.getHours() < 6 || now.getHours() >= 19;
/** Maps a WMO weather code onto the ten Station C line icons. Fog has no icon of its own and
 * borrows the wind lines; sleet reads as rain; thunder with hail becomes lightning + rain. */
export function weatherIconName(code: number, now = new Date()): WeatherIconName | null {
  const night = isNightHour(now);
  if (code === 0) return night ? 'moon' : 'clear';
  if (code === 1 || code === 2) return night ? 'cloud-moon' : 'slightly-cloudy';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'wind';
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 68, 69, 80, 81, 82].includes(code)) return 'rain';
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'snow';
  if (code === 95) return 'lightning';
  if (code === 96 || code === 99) return 'lightning-rain';
  return null;
}
export function parseWeatherObservation(value: unknown): WeatherObservation | null {
  if (!value || typeof value !== 'object') return null;
  const w = value as WeatherObservation;
  const num = (n: number, min: number, max: number) => typeof n === 'number' && Number.isFinite(n) && n >= min && n <= max;
  if (typeof w.placeName !== 'string' || !w.placeName.trim() || w.placeName.length > 100 || !num(w.temperatureC, -100, 70)
    || !num(w.humidityPercent, 0, 100) || !num(w.windKmh, 0, 600) || !Number.isInteger(w.code) || !weatherCondition(w.code)) return null;
  return { placeName: w.placeName.replace(/[\u0000-\u001f\u007f]/g, ' '), temperatureC: w.temperatureC,
    humidityPercent: w.humidityPercent, windKmh: w.windKmh, code: w.code };
}
export function weatherSummary(data: NoticeWidgetData | null): string {
  const w = parseWeatherObservation(data?.weather);
  return w ? `${w.placeName} ${weatherCondition(w.code)} ${w.temperatureC}°C 습도 ${w.humidityPercent}% 풍속 ${w.windKmh} km/h`
    : data?.message || '날씨를 불러오는 중';
}

/** The editor, thumbnail and Player share measured rows, so alignment follows
 * visible content rather than a fixed, mostly empty 600px template.
 * `backdrop` is the colour behind the widget (canvas fill) used to knock out icon overlaps. */
export function drawWeatherWidget(ctx: CanvasRenderingContext2D, layer: NoticeDataWidgetLayer, data: NoticeWidgetData | null, backdrop = '#000000'): WeatherWidgetSize {
  const hiddenWeather = layer.hideWhenUnavailable && data?.status !== 'ready';
  const w = hiddenWeather ? null : parseWeatherObservation(data?.weather);
  const fields = hiddenWeather
    ? { place: false, icon: layer.weatherFields?.icon !== false, temperature: false, humidity: false, wind: false }
    : layer.weatherFields ?? {};
  const font = Math.max(8, layer.fontSize), gap = font * 0.8;
  const align = layer.align ?? 'left', family = cssFontFamily(layer.fontFamily);
  const number = (n: number) => new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(n);
  type Tile = { width: number; height: number; draw(x: number, y: number): void };
  ctx.save(); ctx.beginPath(); ctx.rect(layer.x, layer.y, layer.width, layer.height); ctx.clip();
  if (layer.backgroundColor) { ctx.fillStyle = layer.backgroundColor; ctx.fillRect(layer.x, layer.y, layer.width, layer.height); }
  ctx.fillStyle = layer.color; ctx.strokeStyle = layer.color; ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
  const offset = (available: number, width: number) => align === 'right' ? available - width : align === 'center' ? (available - width) / 2 : 0;
  const text = (value: string, size: number, weight = 400, alpha = 1): Tile => {
    ctx.font = `${weight} ${size}px ${family}`;
    // Long place names must not force the entire weather block to become tiny.
    const maxWidth = font * 20; let fitted = value;
    while (fitted.length > 1 && ctx.measureText(fitted).width > maxWidth) fitted = fitted.slice(0, -1);
    if (fitted !== value) fitted = fitted.slice(0, -1) + '…';
    const width = ctx.measureText(fitted).width;
    return { width, height: size * 1.2, draw(x, y) {
      ctx.save(); ctx.globalAlpha *= alpha; ctx.font = `${weight} ${size}px ${family}`;
      ctx.fillText(fitted, x, y + size); ctx.restore();
    } };
  };
  const row = (items: Tile[]): Tile => ({ width: items.reduce((sum, item) => sum + item.width, 0) + Math.max(0, items.length - 1) * gap,
    height: Math.max(0, ...items.map(item => item.height)), draw(x, y) {
      const height = Math.max(0, ...items.map(item => item.height));
      for (const item of items) { item.draw(x, y + (height - item.height) / 2); x += item.width + gap; }
    } });
  const metric = (label: string, value: string): Tile => {
    const title = text(label, font * 0.65, 400, 0.72), reading = text(value, font, 500);
    const width = Math.max(title.width, reading.width);
    return { width, height: title.height + reading.height + font * 0.15, draw(x, y) {
      title.draw(x + offset(width, title.width), y); reading.draw(x + offset(width, reading.width), y + title.height + font * 0.15);
    } };
  };
  const iconScale = font * 3 / WEATHER_ICON_VIEWBOX.width;
  const icon: Tile = { width: font * 3, height: WEATHER_ICON_VIEWBOX.height * iconScale, draw(x, y) {
    ctx.save(); ctx.translate(x, y); ctx.scale(iconScale, iconScale);
    if (hiddenWeather) {
      ctx.font = `400 64px ${family}`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('-', WEATHER_ICON_VIEWBOX.width / 2, WEATHER_ICON_VIEWBOX.height / 2);
    } else weatherIcon(ctx, w ? weatherIconName(w.code) : null, layer.color, layer.backgroundColor ?? backdrop);
    ctx.restore();
  } };
  const temperature = text(w ? `${number(w.temperatureC)}°` : '_', font * 3, 500);
  const condition = text(w ? weatherCondition(w.code)! : '_', font, 500), showCondition = fields.condition === true;
  const metrics: Tile[] = [];
  if (fields.humidity !== false) metrics.push(metric('습도', w ? `${number(w.humidityPercent)}%` : '_'));
  if (fields.wind !== false) metrics.push(metric('바람', w ? `${number(w.windKmh)} km/h` : '_'));
  const place = w?.placeName ?? ((layer.source.kind === 'weather' ? layer.source.placeName : '') || '지역 미선택');
  const placeTile = text(place, font, 500), rows: Tile[] = [];
  if (layer.weatherLayout === 'horizontal') {
    const body = [...(fields.place !== false ? [placeTile] : []), ...(fields.icon !== false ? [icon] : []), ...(showCondition ? [condition] : []),
      ...(fields.temperature !== false ? [temperature] : []), ...metrics];
    if (body.length) rows.push(row(body));
  } else {
    if (fields.place !== false) rows.push(placeTile);
    if (fields.icon !== false) rows.push(icon);
    if (showCondition) rows.push(condition);
    if (fields.temperature !== false) rows.push(temperature);
    if (metrics.length) rows.push(row(metrics));
  }
  const footer = w && data?.status === 'stale' ? '갱신 지연' : '';
  if (footer) rows.push(text(footer, font * 0.55, 400, 0.65));
  if (!rows.length) { ctx.restore(); return { width: 0, height: 0 }; }
  const width = Math.max(...rows.map(item => item.width), 1), height = rows.reduce((sum, item) => sum + item.height, 0) + gap * (rows.length - 1);
  const padding = Math.min(font * 0.5, layer.width * 0.03, layer.height * 0.03);
  const scale = Math.max(0.001, Math.min(1, (layer.width - padding * 2) / width, (layer.height - padding * 2) / height));
  const available = (layer.width - padding * 2) / scale;
  ctx.translate(layer.x + padding, layer.y + (layer.height - height * scale) / 2); ctx.scale(scale, scale);
  let y = 0;
  for (const item of rows) { item.draw(offset(available, item.width), y); y += item.height + gap; }
  ctx.restore();
  return { width: width + padding * 2, height: height + padding * 2 };
}
const iconPaths = new Map<string, Path2D>();
/** Draws one Station C line icon in its 100×88 design space. Without weather data (or
 * without Path2D, e.g. in tests) a plain cloud outline stands in as the placeholder. */
function weatherIcon(ctx: CanvasRenderingContext2D, name: WeatherIconName | null, color: string, backdrop: string) {
  if (typeof Path2D === 'undefined') return;
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = color;
  const entries = WEATHER_ICON_PATHS[name ?? 'cloudy'];
  for (const entry of entries) {
    if (name === null && entry.knockout) continue; // placeholder: single outline only
    let path = iconPaths.get(entry.d);
    if (!path) { path = new Path2D(entry.d); iconPaths.set(entry.d, path); }
    if (entry.knockout) { ctx.fillStyle = backdrop; ctx.fill(path); }
    ctx.lineWidth = entry.width; ctx.stroke(path);
  }
}
